import { useQuery } from '@apollo/client';
import {
  FormControl,
  FormHelperText,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '~/contexts';
import { useUniqueId } from '~/hooks/unique-id';
import { DataSourceSelectListDocument } from './DataSourceSelect.generated';

export interface DataSourceSelectProps {
  'aria-label': string;
  disabled: boolean;
  name: string;
  onChange: (event: SelectChangeEvent) => void;
  error: boolean;
  helperText: string;
  nullable: boolean;
  value: string;
}

export const DataSourceSelect = ({
  'aria-label': ariaLabel,
  error,
  helperText,
  nullable,
  value,
  ...inputProps
}: DataSourceSelectProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const helperId = useUniqueId('data-source-helper');

  const { data: sourcesQuery } = useQuery(DataSourceSelectListDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const dataSources = useMemo(() => sourcesQuery?.network?.allDataSources ?? [], [sourcesQuery]);

  return (
    <FormControl error={error} fullWidth>
      <Select
        {...inputProps}
        aria-describedby={helperId}
        // On Select itself the label lands on the wrapper, not the combobox.
        inputProps={{ 'aria-label': ariaLabel }}
        value={String(value)}
        displayEmpty
        fullWidth
      >
        {nullable && <MenuItem value="">{t('common.labels.none')}</MenuItem>}
        {dataSources.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            <ListItemText primary={item.name} />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText id={helperId}>{helperText}</FormHelperText>
    </FormControl>
  );
};
